import * as React from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { TIME_INTERVALS, type SupportedPortalLocale } from "@heglo/ui/tokens";

interface TimelineItem {
  icon: React.ReactNode;
  title: string;
  description?: string;
  timestamp: string | Date;
  color?: "primary" | "success" | "warning" | "danger";
}

interface ActivityTimelineProps extends React.HTMLAttributes<HTMLDivElement> {
  items: TimelineItem[];
  maxItems?: number;
}

const colorClasses = {
  primary: {
    bg: "bg-primary/10",
    border: "border-primary",
    text: "text-primary",
  },
  success: {
    bg: "bg-success/10",
    border: "border-success",
    text: "text-success",
  },
  warning: {
    bg: "bg-warning/10",
    border: "border-warning",
    text: "text-warning",
  },
  danger: {
    bg: "bg-danger/10",
    border: "border-danger",
    text: "text-danger",
  },
};

function formatTimestamp(timestamp: string | Date, locale: SupportedPortalLocale): string {
  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
  const differenceMs = date.getTime() - Date.now();
  const absoluteDifference = Math.abs(differenceMs);
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (absoluteDifference < TIME_INTERVALS.minuteMs) return formatter.format(0, 'second');
  if (absoluteDifference < TIME_INTERVALS.hourMs) return formatter.format(Math.round(differenceMs / TIME_INTERVALS.minuteMs), 'minute');
  if (absoluteDifference < TIME_INTERVALS.dayMs) return formatter.format(Math.round(differenceMs / TIME_INTERVALS.hourMs), 'hour');
  if (absoluteDifference < TIME_INTERVALS.relativeDateCutoffDays * TIME_INTERVALS.dayMs) {
    return formatter.format(Math.round(differenceMs / TIME_INTERVALS.dayMs), 'day');
  }

  return date.toLocaleDateString(locale === 'ar' ? "ar-SA" : "en-US", { numberingSystem: 'latn',
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const ActivityTimeline = React.forwardRef<HTMLDivElement, ActivityTimelineProps>(
  ({ items, maxItems, className, ...props }, ref) => {
    const { t, i18n } = useTranslation();
    const locale: SupportedPortalLocale = i18n.language.startsWith('ar') ? 'ar' : 'en';
    const displayItems = maxItems ? items.slice(0, maxItems) : items;

    return (
      <div ref={ref} className={cn("space-y-0", className)} {...props}>
        {displayItems.map((item, index) => {
          const color = item.color || "primary";
          const colorClass = colorClasses[color];
          const isLast = index === displayItems.length - 1;

          return (
            <div key={index} className="relative flex gap-4">
              {/* Timeline Line */}
              {!isLast && (
                <div className="absolute start-5 top-10 bottom-0 w-px bg-border" />
              )}

              {/* Icon */}
              <div
                className={cn(
                  "relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2",
                  colorClass.bg,
                  colorClass.border
                )}
              >
                <div className={cn("h-5 w-5", colorClass.text)}>{item.icon}</div>
              </div>

              {/* Content */}
              <div className="flex-1 pb-6">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-sm">{item.title}</p>
                    {item.description && (
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {item.description}
                      </p>
                    )}
                  </div>
                  <time className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatTimestamp(item.timestamp, locale)}
                  </time>
                </div>
              </div>
            </div>
          );
        })}

        {maxItems && items.length > maxItems && (
          <p className="text-sm text-muted-foreground text-center pt-2">
            {t('common.moreEvents', { count: items.length - maxItems })}
          </p>
        )}
      </div>
    );
  }
);
ActivityTimeline.displayName = "ActivityTimeline";

export { ActivityTimeline };
export type { ActivityTimelineProps, TimelineItem };
